import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TranslocoPipe } from '@jsverse/transloco';
import { ENTITY_TABLE_WIDGET, EntityTableColumn, EntityTableTotal, WIDGET_REGISTRY, WidgetHostComponent, WidgetPortEvent, WidgetRegistration } from '@processpuzzle/widgets';
import { firstValueFrom } from 'rxjs';
import { DocumentBlock, BlockKind, DocumentInputPort, WidgetPlacement } from '../../domain/base-document';
import { DocumentPathCatalog } from '../report/document-path-catalog';
import { WidgetBlockSettings, WidgetBlockSettingsData, WidgetBlockSettingsDialogComponent } from './widget-block-settings-dialog.component';
import { EntityTableSettings, EntityTableSettingsData, EntityTableSettingsDialogComponent } from './entity-table-settings-dialog.component';
import { DocumentContentStore } from './document-content.store';
import { DocumentTextBlockComponent } from './document-text-block.component';

/**
 * The primary editing surface for a document's content — see BaseDocumentContainerComponent
 * for how this sits alongside the generic Properties form on one screen. Renders the flat
 * block list in order: TEXT blocks get their own DocumentTextBlockComponent/Editor instance,
 * STANDALONE WIDGET blocks mount through base-widget's WidgetHostComponent. REFERENCED WIDGET blocks are
 * deliberately absent from this top-level list — they only ever render inside whichever TEXT
 * block's Tiptap content embeds them via widgetEmbed (see DocumentTextBlockComponent), which is
 * the one place base-document-api.yaml lets a REFERENCED block appear.
 */
@Component({
  selector: 'pp-document-editor',
  standalone: true,
  imports: [CommonModule, DocumentTextBlockComponent, WidgetHostComponent, MatButton, MatIcon, MatIconButton, MatMenu, MatMenuItem, MatMenuTrigger, MatTooltip, TranslocoPipe],
  providers: [DocumentContentStore],
  template: `
    <div class="pp-document-editor">
      @for (block of standaloneBlocks(); track block.id) {
        @switch (block.kind) {
          @case (blockKindText) {
            <pp-document-text-block [block]="block" />
          }
          @case (blockKindWidget) {
            <div class="pp-document-editor__configurable">
              <pp-widget-host [widget]="block" [bindingResolver]="resolveBinding" (portEmit)="onPortEmit($event)" />
              @if (block.type === entityTableType) {
                <button
                  mat-icon-button
                  type="button"
                  class="pp-document-editor__settings"
                  [attr.data-testid]="'document-table-settings-' + block.id"
                  [matTooltip]="'base_document.document.report.table_settings' | transloco"
                  (click)="onConfigureTable(block)"
                >
                  <mat-icon>tune</mat-icon>
                </button>
              } @else {
                <button
                  mat-icon-button
                  type="button"
                  class="pp-document-editor__settings"
                  [attr.data-testid]="'document-widget-settings-' + block.id"
                  [matTooltip]="'base_document.document.widget_settings.title' | transloco"
                  (click)="onConfigureWidget(block)"
                >
                  <mat-icon>tune</mat-icon>
                </button>
              }
            </div>
          }
        }
      }

      <div class="pp-document-editor__actions">
        <button mat-stroked-button type="button" data-testid="document-add-text-block" [disabled]="isAppending()" (click)="onAddTextBlock()">
          <mat-icon>add</mat-icon>
          {{ 'base_document.document.content.add_text_block' | transloco }}
        </button>
        @if (widgetTypes().length > 0) {
          <button mat-stroked-button type="button" data-testid="document-add-widget-block" [disabled]="isAppending()" [matMenuTriggerFor]="widgetMenu">
            <mat-icon>widgets</mat-icon>
            {{ 'base_document.document.content.add_widget_block' | transloco }}
          </button>
          <mat-menu #widgetMenu>
            @for (widget of widgetTypes(); track widget.type) {
              <button mat-menu-item type="button" (click)="onAddWidgetBlock(widget.type)">{{ widget.definition.name }}</button>
            }
          </mat-menu>
        }
      </div>

      @if (appendError()) {
        <p class="pp-document-editor__error" role="alert">{{ appendError() }}</p>
      }
    </div>
  `,
  styles: [
    `
      .pp-document-editor__configurable {
        position: relative;
        min-height: 40px;
      }
      .pp-document-editor__settings {
        position: absolute;
        top: 0;
        right: 0;
      }
      .pp-document-editor__actions {
        display: flex;
        gap: 8px;
        margin-top: 16px;
      }
    `,
  ],
})
export class DocumentEditorComponent implements OnChanges {
  readonly documentId = input.required<string>();
  /** Locale whose draft is edited. Blocks are stored per locale — see DocumentContentService. */
  readonly locale = input.required<string>();
  readonly blocks = input.required<DocumentBlock[]>();
  /** False when the locale has no draft yet, so the first append creates one. See DocumentContentStore. */
  readonly translationExists = input<boolean>(true);
  /** The document's input ports: where data fields and tables take their paths from. */
  readonly inputPorts = input<readonly DocumentInputPort[]>([]);

  private readonly widgetRegistry = inject(WIDGET_REGISTRY, { optional: true }) ?? new Map<string, WidgetRegistration>();
  private readonly contentStore = inject(DocumentContentStore);
  private readonly dialog = inject(MatDialog);
  private readonly pathCatalog = inject(DocumentPathCatalog);

  protected readonly entityTableType = ENTITY_TABLE_WIDGET;

  protected readonly blockKindText = BlockKind.TEXT;
  protected readonly blockKindWidget = BlockKind.WIDGET;
  protected readonly isAppending = signal(false);
  protected readonly appendError = signal<string | undefined>(undefined);
  protected readonly widgetTypes = computed(() => [...this.widgetRegistry.values()]);

  /**
   * Re-seeds the content store whenever a different document is loaded — cheap, since
   * DocumentContentStore.initialize() is just two signal writes.
   *
   * A lifecycle hook rather than a computed because `initialize()` writes signals, which a reactive
   * computation may not do (NG0600), and rather than an `effect()` because a view effect runs after
   * the template of the pass that registered it (`refreshView` calls `executeTemplate` before
   * `runEffectsInView`) — the blocks would render empty once and only appear on the dirty re-run.
   * `ngOnChanges` is a pre-order hook of the *parent* view, so it lands before this component's
   * template is first executed, and unlike `ngOnInit` it runs again when the container swaps a
   * different document into the same editor instance.
   */
  ngOnChanges(changes?: SimpleChanges): void {
    // The ports arrive on their own — the document entity often loads after its content — and must not
    // re-seed the blocks, which would drop every edit made since the first seeding.
    const onlyPortsChanged = changes !== undefined && Object.keys(changes).every((name) => name === 'inputPorts');
    if (!onlyPortsChanged) this.contentStore.initialize(this.documentId(), this.locale(), this.blocks(), this.translationExists());
    this.contentStore.inputPorts.set(this.inputPorts());
  }

  // Renders from the store's own live signal (not the `blocks` input directly), so edits made
  // through DocumentContentStore — append/delete/reorder — are reflected without the parent
  // needing to re-fetch the whole document.
  protected readonly standaloneBlocks = computed(() => this.contentStore.blocks().filter((b) => b.kind !== BlockKind.WIDGET || b.placement !== WidgetPlacement.REFERENCED));

  /** An arrow, so the host can call it unbound. Bindings resolve against the store — see its own note. */
  protected readonly resolveBinding = (portName: string): unknown => this.contentStore.resolveBinding(portName);

  protected onPortEmit(event: WidgetPortEvent): void {
    this.contentStore.publishOutput(event.port, event.value);
  }

  /**
   * The one path by which a document that has no content can get some — without it the editor is a dead
   * end for every document whose locale has never been written, which is every newly created one.
   */
  async onAddTextBlock(): Promise<void> {
    await this.append(() => this.contentStore.appendTextBlock());
  }

  async onAddWidgetBlock(widgetType: string): Promise<void> {
    await this.append(() => this.contentStore.appendStandaloneWidget(widgetType));
  }

  /**
   * Surfaced in the editor rather than left to the global HTTP error snackbar: appending is the action the
   * user just took here, and a 403 on a document they may not edit has to be visible next to the button
   * that failed. Guarded against a second concurrent append, which would race two POSTs onto the same draft.
   */
  private async append(appendBlock: () => Promise<string>): Promise<void> {
    if (this.isAppending()) return;

    this.isAppending.set(true);
    this.appendError.set(undefined);
    try {
      await appendBlock();
    } catch (error) {
      this.appendError.set(describeError(error));
    } finally {
      this.isAppending.set(false);
    }
  }

  /** Opens the table settings for one `entity-table` block and saves what comes back as its props and binding. */
  async onConfigureTable(block: DocumentBlock): Promise<void> {
    const { collections } = await this.pathCatalog.describe(this.contentStore.inputPorts());
    const props = block.props ?? {};
    const current: EntityTableSettings = {
      source: block.inputBindings?.['rows'],
      columns: (props['columns'] as EntityTableColumn[] | undefined) ?? [],
      totals: (props['totals'] as EntityTableTotal[] | undefined) ?? [],
      caption: props['caption'] as string | undefined,
    };
    const dialogRef = this.dialog.open<EntityTableSettingsDialogComponent, EntityTableSettingsData, EntityTableSettings>(EntityTableSettingsDialogComponent, { data: { collections, current } });
    const settings = await firstValueFrom(dialogRef.afterClosed());
    if (!settings?.source) return;
    const { caption, columns, totals } = settings;
    await this.contentStore.saveWidgetBlock(block.id, {
      props: { ...props, columns, totals, ...(caption ? { caption } : {}) },
      inputBindings: { ...block.inputBindings, rows: settings.source },
    });
  }

  /**
   * Opens the settings of any other widget block: its props, from the widget's schema, and which document path
   * feeds each of its input ports. A block whose widget is not registered is edited as the raw props it carries.
   */
  async onConfigureWidget(block: DocumentBlock): Promise<void> {
    const { collections, fields } = await this.pathCatalog.describe(this.contentStore.inputPorts());
    const definition = this.widgetRegistry.get(block.type ?? '')?.definition;
    const data: WidgetBlockSettingsData = {
      name: definition?.name ?? block.type ?? '',
      schema: definition?.propsSchema,
      inputPorts: definition?.inputPorts ?? [],
      props: block.props ?? {},
      inputBindings: block.inputBindings ?? {},
      paths: [...new Set([...collections.map((each) => each.path), ...fields.map((each) => each.path)])],
    };
    const dialogRef = this.dialog.open<WidgetBlockSettingsDialogComponent, WidgetBlockSettingsData, WidgetBlockSettings>(WidgetBlockSettingsDialogComponent, { data });
    const settings = await firstValueFrom(dialogRef.afterClosed());
    if (!settings) return;
    await this.contentStore.saveWidgetBlock(block.id, { props: settings.props, inputBindings: settings.inputBindings });
  }

  /** Called by a toolbar action to embed a new widget at the given text block's cursor. */
  async insertWidgetInto(textBlock: DocumentTextBlockComponent, widgetType: string, props: Record<string, unknown> = {}) {
    const blockId = await this.contentStore.appendReferencedWidget(widgetType, props);
    textBlock.embedWidget(blockId);
  }
}

/**
 * What went wrong, in the one line the alert has room for. A failed append is almost always an
 * `HttpErrorResponse`, which carries a `message` but is not an `Error` — so testing for `Error` alone left
 * the user reading `[object Object]` in place of the status the request came back with.
 */
function describeError(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'message' in error) return String((error as { message: unknown }).message);
  return String(error);
}
